import { showSuccess, showError } from '@calendly/alert';
import { useFlag } from '@calendly/feature-check-react';
import { Form } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { useEditorContent } from '@calendly/rich-text-editor';
import { BareButton } from '@calendly/ui/components/bare-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { SimplePopup } from '@calendly/ui/components/popup';
import { Sheet } from '@calendly/ui/components/sheet';
import { HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useState, useContext, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useGetUserQuery, useGetOrganizationUuid } from 'common';
import LoadingState from 'components/core/loading_state';
import Settings from 'core/settings';
import { useGetMeetingQuery, useGetInviteesQuery } from 'features/meetings';
import { TemplateSummaryContext } from 'features/tracked_meetings/template_summary_context';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events';
import { useSendMeetingShareEmailMutation, useFetchAttendeeFollowUpEmailQuery } from 'hooks/meeting_shares/api';
import { useFetchTrackedMeetingWithSummaryQuery } from 'hooks/tracked_meetings/api';
import ShareRecap from 'images/tracked_meetings/share-recap.svg';
import { ShareContext } from '../context';
import { validationSchema } from './config';
import { Footer } from './footer';
import { SendFromCalendlyEmailForm } from './form';
import { createEmailBody, createAttendeeEmailBody, parseInvitees } from './helpers';
const shareContainerCss = "stessbk";
const ShareContainer = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(shareContainerCss, props.className)} />);
const shareModalHeaderIsPhoneCss = "s1aqov27";
const shareModalHeaderCss = "s6wcqs4";
const ShareModalHeader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(shareModalHeaderCss, {
    [shareModalHeaderIsPhoneCss]: isPhone
  }, props.className)} />;
});
const alignmentContainerCss = "aaeoi1v";
const AlignmentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(alignmentContainerCss, props.className)} />;
});
const modalContentWrapperCss = "mr5qdob";
const ModalContentWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(modalContentWrapperCss, props.className)} />;
});
const styledBareButtonCss = "swoiply";
const StyledBareButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(styledBareButtonCss, props.className)} />);
const styledPopupCss = "s1811a35";
const StyledPopup = forwardRef((props, ref) => <SimplePopup ref={ref} {...props} className={classNames(styledPopupCss, props.className)} />);
export const ShareEmailModal = ({
  onClose,
  onBack,
  emails,
  onShareLinkClick,
  videoPageUrl,
  isConfirmationPopupOpen,
  handleOpenConfirmationPopup,
  handleCloseConfirmationPopup,
  handleConfirmAction,
  closeModal,
  isAttendeeModal,
  attendeesFromSearchData
}) => {
  const {
    share,
    shareUuid,
    eventUuid: meetingUuid
  } = useContext(ShareContext);
  const {
    summaries: templateSummaries
  } = useContext(TemplateSummaryContext);
  const {
    accessLevel,
    passcode: videoPasscode
  } = share;
  const isMiEnabled = useFlag('meeting_intelligence');
  const [isEditorReady, {
    fromText: textToEditorContent,
    fromHTML: htmlToEditorContent
  }] = useEditorContent();
  const {
    data: trackedMeeting,
    isLoading: trackedMeetingLoading
  } = useFetchTrackedMeetingWithSummaryQuery({
    meetingUuid
  });
  const isCalendlyMeeting = trackedMeeting?.foreignEventProvider === 'Calendly';
  const summaries = isMiEnabled ? templateSummaries : trackedMeeting?.summaries;
  const trackedMeetingForeignEventId = trackedMeeting?.foreignEventId;
  const {
    data: event
  } = useGetMeetingQuery({
    eventId: trackedMeetingForeignEventId,
    selectedView: 'scheduled'
  }, {
    skip: trackedMeeting?.foreignEventProvider !== 'Calendly'
  });
  const [invitees, setInvitees] = useState(null);
  const {
    data: inviteesData,
    isSuccess: inviteesSuccess
  } = useGetInviteesQuery({
    eventId: event?.id,
    page: 1
  }, {
    skip: !event || !isCalendlyMeeting
  });
  useEffect(() => {
    if (isCalendlyMeeting) {
      if (inviteesSuccess) setInvitees(inviteesData);
    } else {
      setInvitees(trackedMeeting?.participants.filter(participant => participant.role !== 'host'));
    }
  }, [inviteesSuccess, inviteesData, isCalendlyMeeting, trackedMeeting?.participants]);
  const [sendMeetingShareEmail] = useSendMeetingShareEmailMutation();
  const {
    data: user
  } = useGetUserQuery();
  const organizationUuid = useGetOrganizationUuid();
  const dispatch = useDispatch();
  const [shouldFetchFollowupSummary, setShouldFetchFollowupSummary] = useState(false);
  useEffect(() => {
    if (trackedMeeting && invitees) {
      setShouldFetchFollowupSummary(true);
    }
  }, [trackedMeeting, invitees]);

  // Fields to pass to api for follow up email summary
  const summary = summaries?.summary[0].text;
  const hosts = [user.profile.name];
  const inviteeNamesForFollowup = isCalendlyMeeting ? invitees?.results.map(invitee => invitee.name) : [];
  const {
    data: followupEmail
  } = useFetchAttendeeFollowUpEmailQuery({
    summary,
    event_uuid: meetingUuid,
    hosts,
    invitees: inviteeNamesForFollowup
  }, {
    skip: !shouldFetchFollowupSummary || !isAttendeeModal
  });
  const onSubmitEmail = async formState => {
    const emailStrings = formState.to_addresses.map(({
      email
    }) => email);
    const trackedMeetingOwnerId = user.id;
    const trackedMeetingOwnerUuid = user.uuid;
    // payload for Segment tracking pixel is base64 encoded JSON
    const jsonSegmentPayload = JSON.stringify({
      writeKey: Settings.segment.client_write_key,
      event: TRACKING_EVENT_NAMES.AI_NOTETAKER_ENGAGED,
      userId: String(trackedMeetingOwnerId),
      version: 1,
      properties: {
        step: 'recipient_viewed_summary_email',
        user_id: trackedMeetingOwnerId,
        user_uuid: trackedMeetingOwnerUuid,
        organization_id: user?.organizationMembership?.id,
        organization_uuid: organizationUuid,
        is_calendly_meeting: trackedMeeting?.foreignEventProvider === 'Calendly',
        event_uuid: trackedMeetingForeignEventId,
        share_uuid: shareUuid,
        share_method: 'email',
        mi_event_uuid: meetingUuid
      }
    });
    const encodedSegmentPayload = btoa(jsonSegmentPayload);
    const values = {
      send_with: formState.send_with,
      to_addresses: emailStrings,
      subject: formState.subject.plainText,
      body: formState.body.html,
      video_url: formState.include_public_link ? videoPageUrl : null,
      video_passcode: !formState.include_public_link || formState.exclude_passcode ? null : videoPasscode,
      segment_payload: encodedSegmentPayload
    };
    try {
      await sendMeetingShareEmail(values);
      onShareLinkClick('email');
      dispatch(showSuccess('Email sent successfully'));
      closeModal(onClose);
    } catch (error) {
      dispatch(showError('Something went wrong'));
    }
  };
  const isLoaded = !isAttendeeModal || invitees && followupEmail;
  if (!isEditorReady || !isLoaded || trackedMeetingLoading) {
    return <ShareContainer>
        <ModalContentWrapper>
          <LoadingState />
        </ModalContentWrapper>
      </ShareContainer>;
  }
  const [inviteeNames, inviteeEmails] = parseInvitees(event, isCalendlyMeeting && invitees?.results?.length > 0 ? invitees.results.filter(invitee => invitee.name !== user.name) : attendeesFromSearchData?.filter(attendee => attendee.email !== user.email) || []);
  const initialBody = isAttendeeModal && followupEmail ? createAttendeeEmailBody(trackedMeeting?.name, user.name, inviteeNames, summaries, followupEmail) : createEmailBody(trackedMeeting?.name, user.name, inviteeNames, summaries);
  const initialSubject = I18n.t('publisher.meeting_intelligence.share.default_email_subject', {
    event: trackedMeeting?.name
  });
  const selectedEmail = emails.length > 1 ? emails[1] : emails[0];
  const formInitialValues = {
    send_with: {
      type: selectedEmail.type,
      email: selectedEmail.emailAddress
    },
    to_addresses: isAttendeeModal ? inviteeEmails : [],
    subject: textToEditorContent(initialSubject),
    body: htmlToEditorContent(initialBody),
    include_public_link: true,
    enable_passcode: accessLevel === 'Passcode',
    exclude_passcode: true
  };
  return <>
      <ShareContainer>
        <ShareModalHeader>
          <HeadingText level={2}>
            <AlignmentContainer>
              <img src={ShareRecap} alt="" />
              {I18n.t('publisher.meeting_intelligence.share.modal_header_la')}
            </AlignmentContainer>
          </HeadingText>
          <StyledBareButton onClick={() => handleOpenConfirmationPopup('close')} aria-label={I18n.t('publisher.action.close')}>
            <CloseIcon size="large" />
          </StyledBareButton>
        </ShareModalHeader>
        <ModalContentWrapper>
          <Form initialValues={formInitialValues} validationSchema={validationSchema()} onSubmit={onSubmitEmail}>
            <SendFromCalendlyEmailForm initialBody={initialBody} emails={emails} isLoading={!isLoaded} />
            <Footer onBack={() => handleOpenConfirmationPopup('back')} isLoading={!isLoaded} />
          </Form>
        </ModalContentWrapper>
      </ShareContainer>
      {isConfirmationPopupOpen && isMiEnabled && <StyledPopup isDestructive title={I18n.t('publisher.meeting_intelligence.share.email_modal_close_header')} onConfirm={() => handleConfirmAction(onClose, onBack)} onClose={handleCloseConfirmationPopup} cancelLabel={I18n.t('publisher.meeting_intelligence.share.email_modal_close_cancel')} confirmLabel={I18n.t('publisher.meeting_intelligence.share.email_modal_close_confirm')}>
          {I18n.t('publisher.meeting_intelligence.share.email_modal_close_warning')}
        </StyledPopup>}
      {isConfirmationPopupOpen && !isMiEnabled && <SimplePopup title={I18n.t('publisher.contacts.confirmation_popup.header')} onConfirm={() => handleConfirmAction(onClose, onBack)} onClose={handleCloseConfirmationPopup} cancelLabel={I18n.t('publisher.contacts.confirmation_popup.cancel')} confirmLabel={I18n.t('publisher.contacts.confirmation_popup.confirm')} size="medium">
          {I18n.t('publisher.meeting_intelligence.share.email_modal_close_warning')}
        </SimplePopup>}
    </>;
};

require("./email_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./email_modal.js");